import { Component, inject, ChangeDetectionStrategy, effect, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { AuthService } from '../../services/auth.service';

/** Gravatar image URL: sha-256 hex of the trimmed, lowercased email. d=404 means "no image" for users without a Gravatar. */
async function gravatarUrlFor(email: string, size: number): Promise<string> {
  const bytes = new TextEncoder().encode(email.trim().toLowerCase());
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  const hash = Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
  return `https://gravatar.com/avatar/${hash}?s=${size}&d=404`;
}

@Component({
  selector: 'app-header',
  imports: [RouterLink],
  templateUrl: './app-header.html',
  styleUrl: './app-header.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppHeader {
  private auth = inject(AuthService);
  private router = inject(Router);

  protected readonly user = this.auth.user;
  protected readonly isAuthenticated = this.auth.isAuthenticated;

  // Gravatar for the signed-in email, or null (fall back to initials). Only
  // set after the image is preloaded off-DOM, so users without a Gravatar
  // keep their initials with no broken-image flash.
  protected readonly gravatarUrl = signal<string | null>(null);

  constructor() {
    effect(() => {
      const email = this.user()?.email;
      this.gravatarUrl.set(null);
      if (!email) return;
      void gravatarUrlFor(email, 80).then((url) => {
        const probe = new Image();
        probe.onload = () => {
          // Ignore a probe that finishes after the signed-in user changed.
          if (this.user()?.email === email) this.gravatarUrl.set(url);
        };
        probe.src = url;
      });
    });
  }

  async logout(): Promise<void> {
    await this.auth.signOut();
    void this.router.navigateByUrl('/');
  }
}